/* ============================================================
   YOLANDA — nano-feature

   1. Entrance reveals, fired once on entry. Content that re-hides
      when the reader scrolls back up is a defect, not an effect.

   2. The water figure for stage 05. A field of nanobubbles sits
      under a clouded surface that is genuinely opaque at rest;
      moving across it displaces the surface and the bubbles show
      through, then it heals shut behind you.

      Ported from the scrollcraft prototype, where this was tested
      and confirmed to read as wiping water. Two things there are
      load-bearing and easy to lose:
        - the parting is a narrow CHANNEL (~a finger wide), not a
          crater. Large blobs pile up and read as ink blots.
        - openness is rasterised into a coarse grid once per frame.
          Resolving 1400 bubbles against 180 blobs directly is a
          quarter of a million distance checks every frame.
   ============================================================ */
(function () {
  "use strict";

  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)");

  /* ---------------------------------------------- entrance */

  (function reveals() {
    var targets = [].slice.call(document.querySelectorAll(
      ".nfe__head, .nfe__stage, .nfe__quote, .nfe__prodtext, .nfe__shot," +
      ".nfe__storyhead, .nfe__cols, .nfe__margin, .nfe__kicker," +
      ".nfe__bizhead, .nfe__bizgrid, .nfe__bizcta, .nfe__close"
    ));
    if (!targets.length) return;
    targets.forEach(function (t) { t.setAttribute("data-nfe-in", ""); });

    if (!("IntersectionObserver" in window) || reduce.matches) {
      targets.forEach(function (t) { t.classList.add("in"); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        reveal(e.target);
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    targets.forEach(function (t) { io.observe(t); });

    function reveal(t) { t.classList.add("in"); io.unobserve(t); }

    /* An unrevealed block sits at opacity 0 but its buttons and inputs are
       still in the tab order, so a keyboard user can land on a control nobody
       can see. Focus normally scrolls the thing into view and the observer
       catches up, but not before a frame or two of focus sitting on nothing.
       Reveal on focus as well, and do it on the ancestor that actually holds
       the class. */
    document.addEventListener("focusin", function (e) {
      for (var i = 0; i < targets.length; i++) {
        if (targets[i].contains(e.target) && !targets[i].classList.contains("in")) {
          reveal(targets[i]);
        }
      }
    });
  })();

  /* ---------------------------------------------- the water figure */

  (function water() {
    var canvas = document.querySelector("[data-water-canvas]");
    var hint = document.querySelector("[data-water-hint]");
    if (!canvas) return;

    var ctx = canvas.getContext("2d");
    var veil = document.createElement("canvas"), vctx = veil.getContext("2d");
    var base = document.createElement("canvas"), bctx = base.getContext("2d");
    var sprite = document.createElement("canvas"), sctx = sprite.getContext("2d");

    var W = 0, H = 0, dpr = 1;
    var bubbles = [], parts = [];
    var touched = false, running = false, t = 0, last = 0, autoPt = null;
    var PART_R = 0.078;

    var seed = 20260830;
    function rnd() { seed = (seed * 1664525 + 1013904223) % 4294967296; return seed / 4294967296; }

    function build() {
      bubbles.length = 0;
      for (var i = 0; i < 1200; i++) {
        var k = rnd();
        var r = k < 0.035 ? 0.020 + rnd() * 0.024
              : k < 0.22  ? 0.0095 + rnd() * 0.011
              :             0.0038 + rnd() * 0.0055;
        bubbles.push({ x: rnd(), y: rnd(), r: r,
                       sp: 0.010 + rnd() * 0.038, wob: 0.003 + rnd() * 0.010, ph: rnd() * 6.283 });
      }
    }

    function bakeSprite() {
      var S = 96; sprite.width = sprite.height = S;
      var g = sctx.createRadialGradient(S * 0.38, S * 0.34, S * 0.02, S * 0.5, S * 0.5, S * 0.5);
      g.addColorStop(0, "rgba(255,255,255,0.98)");
      g.addColorStop(0.22, "rgba(226,243,255,0.72)");
      g.addColorStop(0.62, "rgba(126,190,247,0.34)");
      g.addColorStop(0.88, "rgba(58,140,214,0.16)");
      g.addColorStop(1, "rgba(20,90,160,0)");
      sctx.fillStyle = g; sctx.beginPath(); sctx.arc(S / 2, S / 2, S / 2, 0, 6.2832); sctx.fill();
    }

    /* A flat pale rectangle reads as an image that has not loaded, so the
       closed surface carries real cloud texture from the first frame. */
    function bakeSurface() {
      bctx.clearRect(0, 0, W, H);
      var g = bctx.createLinearGradient(0, 0, W * 0.35, H);
      g.addColorStop(0, "#cfe0f0"); g.addColorStop(0.45, "#b3cce4"); g.addColorStop(1, "#93b3d4");
      bctx.fillStyle = g; bctx.fillRect(0, 0, W, H);
      var s2 = 77777;
      function r2() { s2 = (s2 * 1103515245 + 12345) % 2147483648; return s2 / 2147483648; }
      for (var i = 0; i < 60; i++) {
        var cx = r2() * W, cy = r2() * H, cr = (0.07 + r2() * 0.28) * Math.max(W, H);
        var light = r2() < 0.5;
        var cg = bctx.createRadialGradient(cx, cy, 0, cx, cy, cr);
        cg.addColorStop(0, light ? "rgba(255,255,255,0.46)" : "rgba(112,152,194,0.40)");
        cg.addColorStop(1, "rgba(255,255,255,0)");
        bctx.fillStyle = cg; bctx.beginPath(); bctx.arc(cx, cy, cr, 0, 6.2832); bctx.fill();
      }
      var sh = bctx.createLinearGradient(0, 0, 0, H * 0.32);
      sh.addColorStop(0, "rgba(255,255,255,0.34)"); sh.addColorStop(1, "rgba(255,255,255,0)");
      bctx.fillStyle = sh; bctx.fillRect(0, 0, W, H * 0.32);
    }

    function size() {
      var b = canvas.getBoundingClientRect();
      if (!b.width || !b.height) return false;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = Math.round(b.width * dpr), h = Math.round(b.height * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        W = w; H = h;
        canvas.width = W; canvas.height = H;
        veil.width = W; veil.height = H; base.width = W; base.height = H;
        bakeSurface();
      }
      return true;
    }

    var GW = 52, GH = 40, grid = new Float32Array(GW * GH);

    function buildGrid() {
      grid.fill(0);
      for (var i = 0; i < parts.length; i++) {
        var b = parts[i], r = b.r * b.life;
        if (r <= 0) continue;
        var x0 = Math.max(0, Math.floor((b.x - r) * GW)), x1 = Math.min(GW - 1, Math.ceil((b.x + r) * GW));
        var y0 = Math.max(0, Math.floor((b.y - r) * GH)), y1 = Math.min(GH - 1, Math.ceil((b.y + r) * GH));
        var rr = r * r;
        for (var gy = y0; gy <= y1; gy++) {
          var cy = (gy + 0.5) / GH - b.y;
          for (var gx = x0; gx <= x1; gx++) {
            var cx = (gx + 0.5) / GW - b.x, d2 = cx * cx + cy * cy;
            if (d2 >= rr) continue;
            var f = 1 - Math.sqrt(d2) / r;
            var v = grid[gy * GW + gx] + f * f * b.life;
            grid[gy * GW + gx] = v > 1 ? 1 : v;
          }
        }
      }
    }

    function openAt(x, y) {
      var gx = x * GW - 0.5, gy = y * GH - 0.5;
      var x0 = Math.floor(gx), y0 = Math.floor(gy), fx = gx - x0, fy = gy - y0;
      if (x0 < 0) { x0 = 0; fx = 0; } if (x0 > GW - 2) { x0 = GW - 2; fx = 1; }
      if (y0 < 0) { y0 = 0; fy = 0; } if (y0 > GH - 2) { y0 = GH - 2; fy = 1; }
      var i0 = y0 * GW + x0, i1 = i0 + GW;
      return (grid[i0] * (1 - fx) + grid[i0 + 1] * fx) * (1 - fy) +
             (grid[i1] * (1 - fx) + grid[i1 + 1] * fx) * fy;
    }

    function addPart(x, y, r) {
      parts.push({ x: x, y: y, r: r, life: 1 });
      if (parts.length > 180) parts.splice(0, parts.length - 180);
    }
    function channel(ax, ay, bx, by, r) {
      var dx = bx - ax, dy = by - ay;
      var steps = Math.min(Math.ceil(Math.hypot(dx, dy) / 0.014), 24);
      for (var i = 1; i <= steps; i++) addPart(ax + dx * (i / steps), ay + dy * (i / steps), r);
    }

    function draw(dt) {
      t += dt;
      for (var i = parts.length - 1; i >= 0; i--) {
        parts[i].life -= dt * 0.30;
        if (parts[i].life <= 0) parts.splice(i, 1);
      }

      /* Nobody has touched it yet: a slow sweep crosses the plate so the
         figure is never a dead pale rectangle, and so touch devices get the
         same meaning without a pointer. */
      if (!touched) {
        var f = (t * 0.13) % 1.35;
        if (f < 1) {
          var ax = 0.08 + f * 0.84, ay = 0.42 + Math.sin(f * 3.1 + 0.4) * 0.20;
          var ar = PART_R * (0.95 + Math.sin(f * 3.14159) * 0.65);
          if (autoPt) channel(autoPt.x, autoPt.y, ax, ay, ar); else addPart(ax, ay, ar);
          autoPt = { x: ax, y: ay };
        } else { autoPt = null; }
      }

      buildGrid();

      var g = ctx.createLinearGradient(0, 0, W * 0.2, H);
      g.addColorStop(0, "#1364a6"); g.addColorStop(0.5, "#0a4478"); g.addColorStop(1, "#062c50");
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);

      var lg = ctx.createRadialGradient(W * 0.34, -H * 0.2, 0, W * 0.34, -H * 0.2, H * 1.25);
      lg.addColorStop(0, "rgba(120,196,255,0.42)"); lg.addColorStop(1, "rgba(120,196,255,0)");
      ctx.fillStyle = lg; ctx.fillRect(0, 0, W, H);

      var drift = reduce.matches ? 0 : t, S = sprite.width;
      for (var b = 0; b < bubbles.length; b++) {
        var o = bubbles[b];
        var by = o.y - drift * o.sp; by -= Math.floor(by);
        var bx = o.x + Math.sin(drift * 0.6 + o.ph) * o.wob;
        var open = openAt(bx, by);
        var pr = o.r * H * (1 + open * 0.20);
        ctx.globalAlpha = 0.38 + open * 0.62;
        ctx.drawImage(sprite, 0, 0, S, S, bx * W - pr, by * H - pr, pr * 2, pr * 2);
      }
      ctx.globalAlpha = 1;

      vctx.globalCompositeOperation = "source-over";
      vctx.clearRect(0, 0, W, H);
      vctx.globalAlpha = 0.74;
      vctx.drawImage(base, 0, 0);
      vctx.globalAlpha = 1;

      var minWH = Math.min(W, H), k;
      for (k = 0; k < parts.length; k++) {   /* the bright lip: displaced water catches light */
        var e = parts[k], er = e.r * minWH * (0.55 + e.life * 0.75);
        if (er <= 0) continue;
        var eg = vctx.createRadialGradient(e.x * W, e.y * H, er * 0.5, e.x * W, e.y * H, er * 1.5);
        eg.addColorStop(0, "rgba(255,255,255,0)");
        eg.addColorStop(0.5, "rgba(255,255,255," + (0.11 * e.life).toFixed(3) + ")");
        eg.addColorStop(1, "rgba(255,255,255,0)");
        vctx.fillStyle = eg; vctx.beginPath(); vctx.arc(e.x * W, e.y * H, er * 1.5, 0, 6.2832); vctx.fill();
      }
      vctx.globalCompositeOperation = "destination-out";
      for (k = 0; k < parts.length; k++) {
        var q = parts[k], qr = q.r * minWH * (0.55 + q.life * 0.75);
        if (qr <= 0) continue;
        var hg = vctx.createRadialGradient(q.x * W, q.y * H, 0, q.x * W, q.y * H, qr);
        hg.addColorStop(0, "rgba(0,0,0," + (0.96 * q.life).toFixed(3) + ")");
        hg.addColorStop(0.40, "rgba(0,0,0," + (0.74 * q.life).toFixed(3) + ")");
        hg.addColorStop(0.72, "rgba(0,0,0," + (0.34 * q.life).toFixed(3) + ")");
        hg.addColorStop(1, "rgba(0,0,0,0)");
        vctx.fillStyle = hg; vctx.beginPath(); vctx.arc(q.x * W, q.y * H, qr, 0, 6.2832); vctx.fill();
      }
      vctx.globalCompositeOperation = "source-over";
      ctx.drawImage(veil, 0, 0);
    }

    var lastPt = null;
    canvas.addEventListener("pointermove", function (e) {
      var b = canvas.getBoundingClientRect();
      var pt = { x: (e.clientX - b.left) / b.width, y: (e.clientY - b.top) / b.height };
      if (pt.x < -0.1 || pt.x > 1.1 || pt.y < -0.1 || pt.y > 1.1) return;
      if (!touched) { touched = true; if (hint) hint.classList.add("is-spent"); }
      if (lastPt) channel(lastPt.x, lastPt.y, pt.x, pt.y, PART_R); else addPart(pt.x, pt.y, PART_R);
      lastPt = pt;
    }, { passive: true });
    canvas.addEventListener("pointerleave", function () { lastPt = null; }, { passive: true });

    function frame(now) {
      var dt = last ? Math.min((now - last) / 1000, 0.05) : 0.016;
      last = now;
      draw(dt);
      if (running) requestAnimationFrame(frame);
    }

    /* Only paint while it is on screen. A canvas animating behind three
       screens of scroll is battery nobody agreed to spend. */
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          if (e.isIntersecting && !running && !reduce.matches) {
            running = true; last = 0; requestAnimationFrame(frame);
          } else if (!e.isIntersecting) { running = false; }
        });
      }, { threshold: 0.05 }).observe(canvas);
    }

    function start() {
      if (!size()) { requestAnimationFrame(start); return; }
      bakeSprite(); build(); draw(0);
      if (reduce.matches) {
        /* no loop: open a generous parting once so the figure still shows
           what it is for, then hold */
        var px = 0.10, py = 0.34;
        for (var i = 1; i <= 26; i++) {
          var f = i / 26, nx = 0.10 + f * 0.80, ny = 0.34 + Math.sin(f * 3.1) * 0.20;
          channel(px, py, nx, ny, PART_R); px = nx; py = ny;
        }
        parts.forEach(function (p) { p.life = 1; });
        draw(0);
        if (hint) hint.classList.add("is-spent");
      }
    }
    window.addEventListener("resize", function () { if (size()) draw(0); }, { passive: true });
    start();
  })();
})();
